import React from "react";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Rating from "@mui/material/Rating";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import CardActions from "@mui/material/CardActions";
import Avatar from "@mui/material/Avatar";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import { useTheme } from "@mui/material/styles";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import { useTranslation } from "react-i18next";
import AlertTitle from "@mui/material/AlertTitle";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { Box } from "@mui/material";
import { postData } from "../../utils/api";
import { UserContext } from "../../App";
import ExperimentTypeSelector from "../experiment/ExperimentTypeSelector";
import demo from "../../locales/de/demoDatasets";

const Alert = React.forwardRef((props, ref) => (
  <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

export default function DemoDatasets() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { fetchDatasets } = React.useContext(UserContext);
  const [datasetUploaded, setDatasetUploaded] = React.useState(false);

  const [snackbarSettings, setSnackbarSettings] = React.useState({
    isOpen: false,
    type: "info",
    message: "",
  });

  const handleDatasetSelection = (fileName) => () => {
    postData(`${process.env.REACT_APP_BACKEND_URL}/api/v0/demodataset`, {
      datasetName: fileName,
      delimiter: ",",
    }).then((data) => {
      const dictCopy = { ...snackbarSettings };
      dictCopy.isOpen = true;

      if (data.status === 201) {
        dictCopy.type = "success";
        dictCopy.message =
          t("datasetCreator.DatasetCreated_3") +
          fileName +
          t("datasetCreator.DatasetCreated_4");
        fetchDatasets({ latest: fileName });
        setDatasetUploaded(true);
      }
      if (data.status === 409) {
        dictCopy.type = "info";
        dictCopy.message = t(
          "datasetConfigurator.ErrorDemoDatasetAlreadyExists"
        );
      }
      setSnackbarSettings(dictCopy);
    });
  };

  return (
    <>
      <Snackbar open={snackbarSettings.isOpen} autoHideDuration={8000}>
        <Alert severity={snackbarSettings.type}>
          {snackbarSettings.message}
        </Alert>
      </Snackbar>

      <Grid
        id="examples-container"
        container
        spacing={4}
        sx={{ padding: theme.spacing(4, 1) }}
      >
        {datasetUploaded ? (
          <>
            <Grid item xs={12}>
              <Alert severity="success">
                <AlertTitle>
                  {t("datasetCreator.DatasetCreated_1")}{" "}
                  {t("datasetCreator.DatasetCreated_2")}
                </AlertTitle>
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <ExperimentTypeSelector theme={theme} />
            </Grid>
          </>
        ) : (
          <>
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontWeight: "bold",
                  textAlign: "center",
                  color: theme.palette.primary.main,
                  mb: 4,
                  borderBottom: `3px solid ${theme.palette.primary.light}`,
                  display: "inline-block",
                  paddingBottom: "0.3rem",
                }}
              >
                {t("demo.PageTitle")}
              </Typography>
            </Grid>

            {Object.keys(demo.examples).map((datasetName) => (
              <Grid key={datasetName} item lg={5} xs={12} sm={6}>
                <Card
                  sx={{
                    maxWidth: 800,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    borderRadius: 5,
                    boxShadow: 3,
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="250"
                    image={t(`demo.examples.${datasetName}.TeaserImageUrl`)}
                    alt="Check out our example experiments."
                  />
                  <CardContent sx={{ minHeight: 550 }}>
                    <Typography variant="h5" gutterBottom>
                      {t(`demo.examples.${datasetName}.Title`)}
                    </Typography>
                    <Typography component="legend">
                      {t("demo.Difficulty")}
                    </Typography>
                    <Rating
                      name="read-only"
                      value={parseInt(
                        t(`demo.examples.${datasetName}.DifficultyRating`),
                        10
                      )}
                      readOnly
                    />
                    <Typography sx={{ mt: 2 }}>
                      {t(`demo.examples.${datasetName}.Description`)}
                    </Typography>

                    <List
                      sx={{
                        width: "100%",
                        paddingTop: theme.spacing(4),
                        maxWidth: 500,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Typography variant="h7">
                        {t("examples.DatasetInformation")}
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "row", p: 2 }}>
                        <ListItemAvatar>
                          <Avatar>
                            <ViewHeadlineIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={t("demo.NumDatapoints")}
                          secondary={t(
                            `demo.examples.${datasetName}.NumDatapoints`
                          )}
                        />
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "row", p: 2 }}>
                        <ListItemAvatar>
                          <Avatar>
                            <ViewColumnIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={t("demo.NumFeatures")}
                          secondary={t(`demo.examples.${datasetName}.Features`)}
                        />
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "row", p: 2 }}>
                        <ListItemAvatar>
                          <Avatar>
                            <ScatterPlotIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={t("demo.TargetDescription")}
                          secondary={t(`demo.examples.${datasetName}.Targets`)}
                        />
                      </Box>
                    </List>
                  </CardContent>
                  <CardActions
                    onClick={handleDatasetSelection(
                      t(`demo.examples.${datasetName}.DatasetFileName`)
                    )}
                    sx={{ padding: 2 }}
                  >
                    <Button
                      variant="outlined"
                      endIcon={<KeyboardArrowRightIcon />}
                      sx={{
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        fontWeight: "bold",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.main,
                          color: "#fff",
                          borderColor: theme.palette.primary.dark,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      {t("common.SelectButton")}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </>
        )}
      </Grid>
    </>
  );
}
